import { useState, useContext, useSelector, useEffect } from "@/common/hooks";
import {
  Heading,
  CommonLink,
  Spinner,
  ErrorMessage,
  InfoBox,
} from "@/common/components";
import { formatGenderCode, formatPhoneNumber } from "@/utils/Utilities";
import { Constants } from "@/utils/Constants";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useGetProductOwnershipJointAnnuitantMutation } from "@/service/OwnerService";
import { setSpouseDetailsInStore } from "@/store/ownersSlice";
import dayjs from "dayjs";

const OwnerDetails = () => {
  const { dispatch, labels, redirectTo } = useContext(PrivateContext);

  const [selectedOwner, setSelectedOwner] = useState();
  const [ownershipInquiryResult, setOwnershipInquiryResult] = useState();

  const {
    firstName,
    middleName,
    lastName,
    suffix,
    addressLine1,
    addressLine2,
    city,
    statePostalCode,
    zipCode,
    phone,
    phoneExt,
    email,
    dateOfBirth,
    genderCode,
    ownerEligibilityCode,
    ownershipPhase,
    maritalStatus,
    employmentStatus,
    enrollmentDate,
    incomeBaseResetDate,
    product,
    withdrawalElectionType,
    withdrawalElectionDate,
  } = selectedOwner || {};

  const { purchaseLockoutPeriodEndDate } = ownershipInquiryResult || {};

  const [
    getProductOwnershipJointAnnuitantApi,
    { isLoading: isFetching, error },
  ] = useGetProductOwnershipJointAnnuitantMutation();

  const selectedOwnerFromStore = useSelector(
    (state) => state?.ownersReducer?.selectedOwner
  );
  const ownershipInquiryFromStore = useSelector(
    (state) => state?.ownersReducer?.ownershipInquiryResult
  );

  const handleSpousalBenefitClick = () => {
    const getProductOwnershipJointAnnuitantPayload = {
      externalProductId: selectedOwner?.product?.externalId,
      lineOfBusinessId: selectedOwner?.lineOfBusinessId,
      recordKeeperName:
        selectedOwner?.recordkeeperRecordkeepingSystem.recordkeeperName,
      recordKeepingSystemName:
        selectedOwner?.recordkeeperRecordkeepingSystem.recordkeepingName,
      planId: selectedOwner?.planId,
      ownershipTypeCode: selectedOwner?.ownershipType,
      ownerTaxId: selectedOwner?.ownerTaxId,
    };

    getProductOwnershipJointAnnuitantApi(
      getProductOwnershipJointAnnuitantPayload
    )
      .unwrap()
      .then((res) => {
        if (!res?.inError) {
          const responseData = res;
          const spousalDetails = {
            maskedAnnuitantTaxId: responseData?.maskedAnnuitantTaxId,
            spouseTaxId: responseData?.annuitantTaxId,
            firstName: responseData?.firstName || "",
            middleName: responseData?.middleName || "",
            lastName: responseData?.lastName || "",
            addressLine1: responseData?.addressLine1 || "",
            addressLine2: responseData?.addressLine2 || "",
            addressLine3: responseData?.addressLine3 || "",
            city: responseData?.city || "",
            stateId: responseData?.addressStateCountry?.id || "",
            zipCode: responseData?.zipCode || "",
            phoneNumber: responseData?.phone || "",
            email: responseData?.email || "",
            civilUnion: responseData?.civilUnionCode || "N",
            genderCode: responseData?.genderCode,
            annuitantBirthDate: responseData?.birthDate || "",
            update: true,
          };
          dispatch(setSpouseDetailsInStore(spousalDetails));
          redirectTo("projectionSpouseDetails");
        }
      });
  };

  const ownerDataFirstColumn = [
    {
      label: labels?.ownerInquiry?.name,
      value: `${firstName} ${middleName} ${lastName}, ${suffix}`,
    },
    {
      label: labels?.ownerInquiry?.street,
      value: (
        <div className="d-flex flex-column">
          <span>{addressLine1}</span>
          <span>{addressLine2}</span>
        </div>
      ),
    },
    { label: labels?.ownerInquiry?.city1, value: city },
    {
      label: labels?.ownerInquiry?.state1,
      value: statePostalCode,
    },
    { label: labels?.ownerInquiry?.zip1, value: zipCode },
    {
      label: labels?.ownerInquiry?.phone1,
      value: phone && formatPhoneNumber(phone),
    },
    { label: labels?.ownerInquiry?.ext, value: phoneExt },
    { label: labels?.ownerInquiry?.email, value: email },
  ];

  const ownerDataSecondColumn = [
    { label: labels?.ownerInquiry?.dob, value: dateOfBirth },
    {
      label: labels?.ownerInquiry?.gender,
      value: genderCode && formatGenderCode(genderCode),
    },
    {
      label: labels?.ownerInquiry?.ownershipPhase,
      value: Constants?.ownerShipPhases[ownershipPhase],
    },
    {
      label: labels?.ownerInquiry?.maritalStatus,
      value: Constants?.maritalStatusCodes[maritalStatus],
    },
    {
      label: labels?.ownerInquiry?.employmentStatus,
      value: Constants?.employmentStatuses[employmentStatus],
    },
    { label: labels?.ownerInquiry?.enrollDate, value: enrollmentDate },
    {
      label: labels?.ownerInquiry?.electionEligibility,
      value: Constants?.electionEligibilityCodes[ownerEligibilityCode],
    },
    {
      label: labels?.ownerInquiry?.incomeBaseReset,
      value: incomeBaseResetDate,
    },
    withdrawalElectionType && {
      label: labels?.ownerInquiry?.withdrawalElectionType,
      value:
        withdrawalElectionType === "J" ? (
          <CommonLink
            click={handleSpousalBenefitClick}
            text={labels?.ownerInquiry?.spousalBenefit}
          />
        ) : (
          labels?.ownerInquiry?.singleBenefit
        ),
    },
    withdrawalElectionDate && {
      label: labels?.ownerInquiry?.withdrawalElectionDate,
      value: withdrawalElectionDate,
    },
    product?.productType &&
      product?.productType === "W" && {
        label: labels?.ownerInquiry?.restrictionEndDate,
        value: purchaseLockoutPeriodEndDate
          ? dayjs(purchaseLockoutPeriodEndDate).format("MM/DD/YYYY")
          : "No Restriction",
      },
  ].filter(Boolean);

  useEffect(() => {
    if (selectedOwnerFromStore) {
      setSelectedOwner(selectedOwnerFromStore);
    }
  }, [selectedOwnerFromStore]);
  useEffect(() => {
    if (ownershipInquiryFromStore) {
      setOwnershipInquiryResult(ownershipInquiryFromStore);
    }
  }, [ownershipInquiryFromStore]);

  return (
    <>
      {isFetching && <Spinner />}
      <ErrorMessage error={error} wrapperClassName="mb-3" />
      <Heading
        text={labels?.ownerInquiry?.ownerDetails}
        wrapperClassNames="h4 border-bottom-1 pb-1 mt-5"
        title={false}
      />

      <div className="d-flex flex-column flex-md-row justify-content-between mt-3 column-gap-5">
        <div className="d-flex flex-column flex-1">
          {ownerDataFirstColumn.map((item, index) => (
            <div className="division" key={index}>
              <span className="left">{item.label}</span>
              <span className="right">{item.value ? item.value : "-"}</span>
            </div>
          ))}
        </div>
        <div className="d-flex flex-column flex-1">
          {ownerDataSecondColumn.map((item, index) => (
            <div className="division" key={index}>
              <span className="left">{item.label}</span>
              <span className="right">{item.value ? item.value : "-"}</span>
            </div>
          ))}
          {selectedOwner?.pendingStatusCode === "Y" && (
            <InfoBox
              message={labels?.ownerInquiry?.electionToWithdrawPending}
              wrapperClassName="my-3"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default OwnerDetails;
